/**
 * Google Places API utility functions using @vis.gl/react-google-maps
 * 
 * This module provides components and functions for interacting with the Google Places API,
 * including address autocomplete and place details retrieval.
 * 
 * Documentation: https://developers.google.com/maps/documentation/javascript/examples/rgm-autocomplete
 * Library: https://www.npmjs.com/package/@vis.gl/react-google-maps
 */
import React, { useState, useEffect, useRef } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';

// API key from environment variables
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Log the API key (without revealing the full key for security)
console.log('Google Maps API Key configured:', API_KEY ? `${API_KEY.substring(0, 6)}...` : 'Not found');

/**
 * Internal PlacesAutocomplete component that uses the useMapsLibrary hook
 */
const PlacesAutocompleteInternal = ({ onPlaceSelect, inputProps = {} }) => {
  const [inputValue, setInputValue] = useState(inputProps.defaultValue || '');
  const [placeAutocomplete, setPlaceAutocomplete] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  
  // Use the places library
  const places = useMapsLibrary('places');
  
  // Log when places library loads
  useEffect(() => {
    console.log('Places library loaded:', !!places);
  }, [places]);
  
  // Initialize autocomplete when places library is loaded
  useEffect(() => {
    if (!places || !inputRef.current) return;
    
    try {
      console.log('Initializing Places Autocomplete...');
      
      const options = {
        fields: [
          'address_components', 
          'formatted_address', 
          'geometry', 
          'name', 
          'place_id',
          'business_status',
          'formatted_phone_number',
          'international_phone_number',
          'opening_hours',
          'photos',
          'rating',
          'types',
          'website'
        ],
        componentRestrictions: { country: 'gb' },
        types: ['establishment'], // Search for all types of businesses/establishments
      };
      
      const autocomplete = new places.Autocomplete(inputRef.current, options);
      console.log('Autocomplete created successfully');
      
      setPlaceAutocomplete(autocomplete);
      setError(null);
    } catch (err) {
      console.error('Error initializing Places Autocomplete:', err);
      setError(`Error: ${err.message}`);
    }
  }, [places]);
  
  // Add event listener for place_changed
  useEffect(() => {
    if (!placeAutocomplete) return;
    
    const listener = placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace();
      console.log('Place selected:', place);
      if (place && onPlaceSelect) {
        onPlaceSelect(place);
      }
    });
    
    return () => {
      if (listener) listener.remove();
    };
  }, [placeAutocomplete, onPlaceSelect]);
  
  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };
  
  // Create a new inputProps object without defaultValue to avoid React warning
  const cleanedInputProps = { ...inputProps };
  delete cleanedInputProps.defaultValue;
  
  return (
    <div>
      {error && (
        <div className="text-red-500 text-sm mb-2">{error}</div>
      )}
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        ref={inputRef}
        placeholder="Search for a dental practice or healthcare business..."
        {...cleanedInputProps}
      />
    </div>
  );
};

/**
 * PlacesAutocomplete component for React using @vis.gl/react-google-maps
 * @param {Object} props - Component props
 * @returns {JSX.Element} The component
 */
export const PlacesAutocomplete = (props) => {
  return (
    <APIProvider apiKey={API_KEY} libraries={['places']}>
      <PlacesAutocompleteInternal {...props} />
    </APIProvider>
  );
};

/**
 * Parse a Google Place result into an address object
 * @param {google.maps.places.PlaceResult} place - The place result from Google Places API
 * @returns {Object} Parsed address object
 */
export const parsePlaceToAddress = (place) => {
  if (!place) {
    console.error('No place result provided');
    return null;
  }
  
  console.log('Parsing place data:', place);
  
  // Initialize the address object with business data
  const address = {
    line1: '',
    line2: '',
    city: '',
    county: '',
    postcode: '',
    country: '',
    latitude: null,
    longitude: null,
    // Business data
    name: place.name || '',
    phone: place.formatted_phone_number || place.international_phone_number || '',
    website: place.website || '',
    businessStatus: place.business_status || '',
    types: place.types || [],
    rating: place.rating || null
  };
  
  // Try to get coordinates from geometry
  try {
    if (place.geometry && place.geometry.location) {
      // Handle both function and property access for lat/lng
      if (typeof place.geometry.location.lat === 'function') {
        address.latitude = place.geometry.location.lat();
        address.longitude = place.geometry.location.lng();
      } else if (place.geometry.location.lat !== undefined) {
        address.latitude = place.geometry.location.lat;
        address.longitude = place.geometry.location.lng;
      }
    }
  } catch (error) {
    console.error('Error extracting coordinates:', error);
  }
  
  // Handle address components from the Places API
  if (place.address_components) {
    // Extract street number and route for line1
    const streetNumber = place.address_components.find(component => 
      component.types.includes('street_number')
    );
    
    const route = place.address_components.find(component => 
      component.types.includes('route')
    );
    
    if (streetNumber && route) {
      address.line1 = `${streetNumber.long_name} ${route.long_name}`;
    } else if (route) {
      address.line1 = route.long_name;
    }
    
    // Extract subpremise for line2
    const subpremise = place.address_components.find(component => 
      component.types.includes('subpremise')
    );
    
    if (subpremise) {
      address.line2 = subpremise.long_name;
    }
    
    // Extract city (locality)
    const locality = place.address_components.find(component => 
      component.types.includes('locality')
    );
    
    if (locality) {
      address.city = locality.long_name;
    }
    
    // Extract county (administrative_area_level_2)
    const county = place.address_components.find(component => 
      component.types.includes('administrative_area_level_2')
    );
    
    if (county) {
      address.county = county.long_name;
    }
    
    // Extract postcode
    const postcode = place.address_components.find(component => 
      component.types.includes('postal_code')
    );
    
    if (postcode) {
      address.postcode = postcode.long_name;
    }
    
    // Extract country
    const country = place.address_components.find(component => 
      component.types.includes('country')
    );
    
    if (country) {
      address.country = country.long_name;
    }
  } 
  // Try to extract from formatted address as a fallback
  else if (place.formatted_address) {
    // Simple parsing of formatted address
    const parts = place.formatted_address.split(',').map(part => part.trim());
    
    if (parts.length >= 1) address.line1 = parts[0];
    if (parts.length >= 2) address.city = parts[1];
    
    // Try to extract postcode using regex (UK postcode format)
    const postcodeRegex = /[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}/i;
    const postcodeMatch = place.formatted_address.match(postcodeRegex);
    if (postcodeMatch) {
      address.postcode = postcodeMatch[0];
    }
    
    // Extract country (usually the last part)
    if (parts.length >= 3) {
      address.country = parts[parts.length - 1];
    }
  }
  
  // If we still don't have a proper address, try to use the place name
  if (!address.line1 && place.name) {
    address.line1 = place.name;
  }
  
  return address;
};
